import { useMemo } from "react";
import {
  TRAYECTOS_PROMEDIO_DIA,
  DISTRIBUCION_FRANJAS,
  HORAS_POR_FRANJA,
  INTERVALOS_MANANA,
  INTERVALOS_MEDIODIA,
  INTERVALOS_TARDE,
  COEFICIENTES_FRECUENCIA,
  CPM_REFERENCIA_BASE,
  DURACION_INCIDENCIA,
  MULTIPLICADOR_TIPO_CAMPANA,
  CPM_RATIO_OTROS_CIRCUITOS,
  DESCUENTO_DURACION,
  BONUS_DURACION_HORAS_LARGAS,
  DESCUENTO_MAXIMO_INVENTARIO,
  INVENTARIO_RATIO_MINIMO,
  DESCUENTO_MAXIMO_PLAZO,
  TAU_AMORTIGUAMIENTO,
  PLAZO_MAXIMO_EFECTIVO,
  PLAZO_MINIMO_DESCUENTO,
  PLAZO_INVENTARIO_BONUS,
  FRECUENCIA_BASE_DESCUENTO,
  FRECUENCIA_INCREMENTO,
  FRECUENCIA_FACTOR_REDUCCION,
  FRECUENCIA_DIVISOR_HORAS_7,
  BONO_FREQ_PLAZO_MULTIPLICADOR,
  PENALIZACION_FREQ_INADECUADA,
  CAP_DESCUENTO_BASE,
  SCALE_FACTOR_PAIS,
} from "../constants";

/**
 * Hook de cálculo principal del cotizador.
 * Toda la lógica es síncrona y determinista: recibe inputs → retorna valores calculados.
 * No tiene efectos secundarios ni muta estado externo.
 */
export function useCalculadoraCalcs({
  ascensoresSeleccionados,
  totemsSeleccionados,
  coworkSeleccionados,
  esperaSeleccionados,
  audienciaSeleccionadaAscensor,
  audienciaSeleccionadaTotems,
  audienciaSeleccionadaCowork,
  audienciaSeleccionadaEspera,
  ascensoresTotal,
  totemsTotal,
  coworkTotal,
  esperaTotal,
  plazo,
  frecuencia,
  horasSeleccionadas,
  duracion,
  pais,
  forceDollars,
  obtenerTipoDeCambio,
  getMonedaSymbol,
  discountType,
  discountPercentage,
  manualDiscountAmount,
  finalAmount,
  forceRecalc,
}) {
  return useMemo(() => {
    const frecuenciaNumero = parseInt(frecuencia, 10) || 0;
    const { intervalos = [], horas = 0, options = [] } = horasSeleccionadas || {};

    // --- Horas final ---
    let horasFinal = 0;
    if (intervalos && intervalos.length > 0) {
      horasFinal = horas || 0;
    } else if (options && options.length > 0) {
      horasFinal = options[0].hours || 0;
    } else {
      horasFinal = 14;
    }

    // --- Salidas por circuito ---
    const salidasAscensores = ascensoresSeleccionados * plazo * frecuenciaNumero * horasFinal;
    const salidasTotems = totemsSeleccionados * plazo * frecuenciaNumero * horasFinal;
    const salidasCowork = coworkSeleccionados * plazo * frecuenciaNumero * horasFinal;
    const salidasEspera = esperaSeleccionados * plazo * frecuenciaNumero * horasFinal;

    // --- Distribución de franjas horarias ---
    const { valle, picoMañana, picoMediodia, picoTarde } = DISTRIBUCION_FRANJAS;

    // --- Contactos totales ---
    const contactosTotalAscensores = audienciaSeleccionadaAscensor * TRAYECTOS_PROMEDIO_DIA;
    const contactosTotalTotems = audienciaSeleccionadaTotems * TRAYECTOS_PROMEDIO_DIA;
    const contactosTotalCowork = audienciaSeleccionadaCowork * TRAYECTOS_PROMEDIO_DIA;
    const contactosTotalEspera = audienciaSeleccionadaEspera * TRAYECTOS_PROMEDIO_DIA;

    // --- Contactos de campaña (audiencia × trayectos/día × plazo) ---
    const contactosCampañaAscensores = audienciaSeleccionadaAscensor * TRAYECTOS_PROMEDIO_DIA * plazo;
    const contactosCampañaTotems = audienciaSeleccionadaTotems * TRAYECTOS_PROMEDIO_DIA * plazo;
    const contactosCampañaCowork = audienciaSeleccionadaCowork * TRAYECTOS_PROMEDIO_DIA * plazo;
    const contactosCampañaEspera = audienciaSeleccionadaEspera * TRAYECTOS_PROMEDIO_DIA * plazo;
    const contactosCampañaTotal =
      contactosCampañaAscensores + contactosCampañaTotems + contactosCampañaCowork + contactosCampañaEspera;

    // Contactos por franja
    const contactosValleAscensores = contactosTotalAscensores * valle;
    const contactosMañanaAscensores = contactosTotalAscensores * picoMañana;
    const contactosMediodiaAscensores = contactosTotalAscensores * picoMediodia;
    const contactosTardeAscensores = contactosTotalAscensores * picoTarde;
    const contactosValleTotems = contactosTotalTotems * valle;
    const contactosMañanaTotems = contactosTotalTotems * picoMañana;
    const contactosMediodiaTotems = contactosTotalTotems * picoMediodia;
    const contactosTardeTotems = contactosTotalTotems * picoTarde;
    const contactosValleCowork = contactosTotalCowork * valle;
    const contactosMañanaCowork = contactosTotalCowork * picoMañana;
    const contactosMediodiaCowork = contactosTotalCowork * picoMediodia;
    const contactosTardeCowork = contactosTotalCowork * picoTarde;
    const contactosValleEspera = contactosTotalEspera * valle;
    const contactosMañanaEspera = contactosTotalEspera * picoMañana;
    const contactosMediodiaEspera = contactosTotalEspera * picoMediodia;
    const contactosTardeEspera = contactosTotalEspera * picoTarde;

    // Contactos por hora
    const contactosVallexHoraAscensores = contactosValleAscensores / HORAS_POR_FRANJA.valle;
    const contactosMañanaxHoraAscensores = contactosMañanaAscensores / HORAS_POR_FRANJA.picoMañana;
    const contactosMediodiaxHoraAscensores = contactosMediodiaAscensores / HORAS_POR_FRANJA.picoMediodia;
    const contactosTardexHoraAscensores = contactosTardeAscensores / HORAS_POR_FRANJA.picoTarde;
    const contactosVallexHoraTotems = contactosValleTotems / HORAS_POR_FRANJA.valle;
    const contactosMañanaxHoraTotems = contactosMañanaTotems / HORAS_POR_FRANJA.picoMañana;
    const contactosMediodiaxHoraTotems = contactosMediodiaTotems / HORAS_POR_FRANJA.picoMediodia;
    const contactosTardexHoraTotems = contactosTardeTotems / HORAS_POR_FRANJA.picoTarde;
    const contactosVallexHoraCowork = contactosValleCowork / HORAS_POR_FRANJA.valle;
    const contactosMañanaxHoraCowork = contactosMañanaCowork / HORAS_POR_FRANJA.picoMañana;
    const contactosMediodiaxHoraCowork = contactosMediodiaCowork / HORAS_POR_FRANJA.picoMediodia;
    const contactosTardexHoraCowork = contactosTardeCowork / HORAS_POR_FRANJA.picoTarde;
    const contactosVallexHoraEspera = contactosValleEspera / HORAS_POR_FRANJA.valle;
    const contactosMañanaxHoraEspera = contactosMañanaEspera / HORAS_POR_FRANJA.picoMañana;
    const contactosMediodiaxHoraEspera = contactosMediodiaEspera / HORAS_POR_FRANJA.picoMediodia;
    const contactosTardexHoraEspera = contactosTardeEspera / HORAS_POR_FRANJA.picoTarde;

    // Contactos por intervalo
    const contactosVallexIntervaloAscensores = contactosVallexHoraAscensores / 2;
    const contactosMañanaxIntervaloAscensores = contactosMañanaxHoraAscensores / 2;
    const contactosMediodiaxIntervaloAscensores = contactosMediodiaxHoraAscensores / 2;
    const contactosTardexIntervaloAscensores = contactosTardexHoraAscensores / 2;
    const contactosVallexIntervaloTotems = contactosVallexHoraTotems / 2;
    const contactosMañanaxIntervaloTotems = contactosMañanaxHoraTotems / 2;
    const contactosMediodiaxIntervaloTotems = contactosMediodiaxHoraTotems / 2;
    const contactosTardexIntervaloTotems = contactosTardexHoraTotems / 2;
    const contactosVallexIntervaloCowork = contactosVallexHoraCowork / 2;
    const contactosMañanaxIntervaloCowork = contactosMañanaxHoraCowork / 2;
    const contactosMediodiaxIntervaloCowork = contactosMediodiaxHoraCowork / 2;
    const contactosTardexIntervaloCowork = contactosTardexHoraCowork / 2;
    const contactosVallexIntervaloEspera = contactosVallexHoraEspera / 2;
    const contactosMañanaxIntervaloEspera = contactosMañanaxHoraEspera / 2;
    const contactosMediodiaxIntervaloEspera = contactosMediodiaxHoraEspera / 2;
    const contactosTardexIntervaloEspera = contactosTardexHoraEspera / 2;

    // Contactos por intervalo por pantalla
    const contactosPorIntervaloPorPantallaValleAscensores = ascensoresSeleccionados
      ? contactosVallexIntervaloAscensores / ascensoresSeleccionados
      : 0;
    const contactosPorIntervaloPorPantallaMañanaAscensores = ascensoresSeleccionados
      ? contactosMañanaxIntervaloAscensores / ascensoresSeleccionados
      : 0;
    const contactosPorIntervaloPorPantallaMediodiaAscensores = ascensoresSeleccionados
      ? contactosMediodiaxIntervaloAscensores / ascensoresSeleccionados
      : 0;
    const contactosPorIntervaloPorPantallaTardeAscensores = ascensoresSeleccionados
      ? contactosTardexIntervaloAscensores / ascensoresSeleccionados
      : 0;
    const contactosPorIntervaloPorPantallaValleTotems = totemsSeleccionados
      ? contactosVallexIntervaloTotems / totemsSeleccionados
      : 0;
    const contactosPorIntervaloPorPantallaMañanaTotems = totemsSeleccionados
      ? contactosMañanaxIntervaloTotems / totemsSeleccionados
      : 0;
    const contactosPorIntervaloPorPantallaMediodiaTotems = totemsSeleccionados
      ? contactosMediodiaxIntervaloTotems / totemsSeleccionados
      : 0;
    const contactosPorIntervaloPorPantallaTardeTotems = totemsSeleccionados
      ? contactosTardexIntervaloTotems / totemsSeleccionados
      : 0;
    const contactosPorIntervaloPorPantallaValleCowork = coworkSeleccionados
      ? contactosVallexIntervaloCowork / coworkSeleccionados
      : 0;
    const contactosPorIntervaloPorPantallaMañanaCowork = coworkSeleccionados
      ? contactosMañanaxIntervaloCowork / coworkSeleccionados
      : 0;
    const contactosPorIntervaloPorPantallaMediodiaCowork = coworkSeleccionados
      ? contactosMediodiaxIntervaloCowork / coworkSeleccionados
      : 0;
    const contactosPorIntervaloPorPantallaTardeCowork = coworkSeleccionados
      ? contactosTardexIntervaloCowork / coworkSeleccionados
      : 0;
    const contactosPorIntervaloPorPantallaValleEspera = esperaSeleccionados
      ? contactosVallexIntervaloEspera / esperaSeleccionados
      : 0;
    const contactosPorIntervaloPorPantallaMañanaEspera = esperaSeleccionados
      ? contactosMañanaxIntervaloEspera / esperaSeleccionados
      : 0;
    const contactosPorIntervaloPorPantallaMediodiaEspera = esperaSeleccionados
      ? contactosMediodiaxIntervaloEspera / esperaSeleccionados
      : 0;
    const contactosPorIntervaloPorPantallaTardeEspera = esperaSeleccionados
      ? contactosTardexIntervaloEspera / esperaSeleccionados
      : 0;

    // --- Contar intervalos por franja ---
    let countValle = 0;
    let countMañana = 0;
    let countMediodia = 0;
    let countTarde = 0;
    if (intervalos) {
      intervalos.forEach((intervalo) => {
        if (INTERVALOS_MANANA.includes(intervalo)) {
          countMañana++;
        } else if (INTERVALOS_MEDIODIA.includes(intervalo)) {
          countMediodia++;
        } else if (INTERVALOS_TARDE.includes(intervalo)) {
          countTarde++;
        } else {
          countValle++;
        }
      });
    }

    // --- Total contactos por periodo ---
    const totalContactosValleAscensores =
      contactosPorIntervaloPorPantallaValleAscensores * countValle * plazo * ascensoresSeleccionados;
    const totalContactosMañanaAscensores =
      contactosPorIntervaloPorPantallaMañanaAscensores * countMañana * plazo * ascensoresSeleccionados;
    const totalContactosMediodiaAscensores =
      contactosPorIntervaloPorPantallaMediodiaAscensores * countMediodia * plazo * ascensoresSeleccionados;
    const totalContactosTardeAscensores =
      contactosPorIntervaloPorPantallaTardeAscensores * countTarde * plazo * ascensoresSeleccionados;
    const totalContactosValleTotems =
      contactosPorIntervaloPorPantallaValleTotems * countValle * plazo * totemsSeleccionados;
    const totalContactosMañanaTotems =
      contactosPorIntervaloPorPantallaMañanaTotems * countMañana * plazo * totemsSeleccionados;
    const totalContactosMediodiaTotems =
      contactosPorIntervaloPorPantallaMediodiaTotems * countMediodia * plazo * totemsSeleccionados;
    const totalContactosTardeTotems =
      contactosPorIntervaloPorPantallaTardeTotems * countTarde * plazo * totemsSeleccionados;
    const totalContactosValleCowork =
      contactosPorIntervaloPorPantallaValleCowork * countValle * plazo * coworkSeleccionados;
    const totalContactosMañanaCowork =
      contactosPorIntervaloPorPantallaMañanaCowork * countMañana * plazo * coworkSeleccionados;
    const totalContactosMediodiaCowork =
      contactosPorIntervaloPorPantallaMediodiaCowork * countMediodia * plazo * coworkSeleccionados;
    const totalContactosTardeCowork =
      contactosPorIntervaloPorPantallaTardeCowork * countTarde * plazo * coworkSeleccionados;
    const totalContactosValleEspera =
      contactosPorIntervaloPorPantallaValleEspera * countValle * plazo * esperaSeleccionados;
    const totalContactosMañanaEspera =
      contactosPorIntervaloPorPantallaMañanaEspera * countMañana * plazo * esperaSeleccionados;
    const totalContactosMediodiaEspera =
      contactosPorIntervaloPorPantallaMediodiaEspera * countMediodia * plazo * esperaSeleccionados;
    const totalContactosTardeEspera =
      contactosPorIntervaloPorPantallaTardeEspera * countTarde * plazo * esperaSeleccionados;

    // --- Total contactos de campaña (para FULL y AM/PM) ---
    let totalContactosCampañaAscensores = 0;
    if (options && options.length > 0 && options[0].label.includes("FULL")) {
      totalContactosCampañaAscensores = audienciaSeleccionadaAscensor * TRAYECTOS_PROMEDIO_DIA * plazo;
    } else if (options && options.length > 0 && (options[0].label.includes("AM") || options[0].label.includes("PM"))) {
      totalContactosCampañaAscensores = ((audienciaSeleccionadaAscensor * TRAYECTOS_PROMEDIO_DIA) / 2) * plazo;
    }

    let totalContactosCampañaTotems = 0;
    if (options && options.length > 0 && options[0].label.includes("FULL")) {
      totalContactosCampañaTotems = audienciaSeleccionadaTotems * TRAYECTOS_PROMEDIO_DIA * plazo;
    } else if (options && options.length > 0 && (options[0].label.includes("AM") || options[0].label.includes("PM"))) {
      totalContactosCampañaTotems = ((audienciaSeleccionadaTotems * TRAYECTOS_PROMEDIO_DIA) / 2) * plazo;
    }

    let totalContactosCampañaCowork = 0;
    if (options && options.length > 0 && options[0].label.includes("FULL")) {
      totalContactosCampañaCowork = audienciaSeleccionadaCowork * TRAYECTOS_PROMEDIO_DIA * plazo;
    } else if (options && options.length > 0 && (options[0].label.includes("AM") || options[0].label.includes("PM"))) {
      totalContactosCampañaCowork = ((audienciaSeleccionadaCowork * TRAYECTOS_PROMEDIO_DIA) / 2) * plazo;
    }

    const totalContactosCampañaEspera =
      options && options.length > 0 && options[0].label.includes("FULL")
        ? audienciaSeleccionadaEspera * TRAYECTOS_PROMEDIO_DIA * plazo
        : options && options.length > 0 && (options[0].label.includes("AM") || options[0].label.includes("PM"))
          ? ((audienciaSeleccionadaEspera * TRAYECTOS_PROMEDIO_DIA) / 2) * plazo
          : 0;

    // --- Coeficientes de frecuencia ---
    const coeficientesFrecuencia = COEFICIENTES_FRECUENCIA;

    // --- Impactos totales por circuito ---
    // Ascensores
    let impactosTotalAscensores = 0;
    if (intervalos && intervalos.length > 0) {
      const coeficientes = coeficientesFrecuencia[frecuenciaNumero] || {};
      if (countValle > 0) impactosTotalAscensores += totalContactosValleAscensores * (coeficientes.valle || 0);
      if (countMañana > 0 || countTarde > 0)
        impactosTotalAscensores +=
          (totalContactosMañanaAscensores + totalContactosTardeAscensores) * (coeficientes.picoMañana || 0);
      if (countMediodia > 0)
        impactosTotalAscensores += totalContactosMediodiaAscensores * (coeficientes.picoMediodia || 0);
    } else if (options && options.length > 0 && options[0].label.includes("FULL")) {
      const coeficientes = coeficientesFrecuencia[frecuenciaNumero] || {};
      impactosTotalAscensores =
        totalContactosCampañaAscensores * valle * (coeficientes.valle || 0) +
        totalContactosCampañaAscensores * picoMañana * (coeficientes.picoMañana || 0) +
        totalContactosCampañaAscensores * picoMediodia * (coeficientes.picoMediodia || 0) +
        totalContactosCampañaAscensores * picoTarde * (coeficientes.picoTarde || 0);
    } else if (options && options.length > 0 && (options[0].label.includes("AM") || options[0].label.includes("PM"))) {
      const coeficientes = coeficientesFrecuencia[frecuenciaNumero] || {};
      impactosTotalAscensores =
        totalContactosCampañaAscensores * valle * (coeficientes.valle || 0) +
        totalContactosCampañaAscensores * picoMañana * (coeficientes.picoMañana || 0) +
        totalContactosCampañaAscensores * picoMediodia * (coeficientes.picoMediodia || 0) +
        totalContactosCampañaAscensores * picoTarde * (coeficientes.picoTarde || 0);
    }
    impactosTotalAscensores = Math.round(impactosTotalAscensores * 100) / 100;

    // Totems
    let impactosTotalTotems = 0;
    if (intervalos && intervalos.length > 0) {
      const coeficientes = coeficientesFrecuencia[frecuenciaNumero] || {};
      if (countValle > 0) impactosTotalTotems += totalContactosValleTotems * (coeficientes.valle || 0);
      if (countMañana > 0 || countTarde > 0)
        impactosTotalTotems +=
          (totalContactosMañanaTotems + totalContactosTardeTotems) * (coeficientes.picoMañana || 0);
      if (countMediodia > 0) impactosTotalTotems += totalContactosMediodiaTotems * (coeficientes.picoMediodia || 0);
    } else if (options && options.length > 0 && options[0].label.includes("FULL")) {
      const coeficientes = coeficientesFrecuencia[frecuenciaNumero] || {};
      impactosTotalTotems =
        totalContactosCampañaTotems * valle * (coeficientes.valle || 0) +
        totalContactosCampañaTotems * picoMañana * (coeficientes.picoMañana || 0) +
        totalContactosCampañaTotems * picoMediodia * (coeficientes.picoMediodia || 0) +
        totalContactosCampañaTotems * picoTarde * (coeficientes.picoTarde || 0);
    } else if (options && options.length > 0 && (options[0].label.includes("AM") || options[0].label.includes("PM"))) {
      const coeficientes = coeficientesFrecuencia[frecuenciaNumero] || {};
      impactosTotalTotems =
        totalContactosCampañaTotems * valle * (coeficientes.valle || 0) +
        totalContactosCampañaTotems * picoMañana * (coeficientes.picoMañana || 0) +
        totalContactosCampañaTotems * picoMediodia * (coeficientes.picoMediodia || 0) +
        totalContactosCampañaTotems * picoTarde * (coeficientes.picoTarde || 0);
    }
    impactosTotalTotems = Math.round(impactosTotalTotems * 100) / 100;

    // Cowork
    let impactosTotalCowork = 0;
    if (intervalos && intervalos.length > 0) {
      const coeficientes = coeficientesFrecuencia[frecuenciaNumero] || {};
      if (countValle > 0) impactosTotalCowork += totalContactosValleCowork * (coeficientes.valle || 0);
      if (countMañana > 0 || countTarde > 0)
        impactosTotalCowork +=
          (totalContactosMañanaCowork + totalContactosTardeCowork) * (coeficientes.picoMañana || 0);
      if (countMediodia > 0) impactosTotalCowork += totalContactosMediodiaCowork * (coeficientes.picoMediodia || 0);
    } else if (options && options.length > 0 && options[0].label.includes("FULL")) {
      const coeficientes = coeficientesFrecuencia[frecuenciaNumero] || {};
      impactosTotalCowork =
        totalContactosCampañaCowork * valle * (coeficientes.valle || 0) +
        totalContactosCampañaCowork * picoMañana * (coeficientes.picoMañana || 0) +
        totalContactosCampañaCowork * picoMediodia * (coeficientes.picoMediodia || 0) +
        totalContactosCampañaCowork * picoTarde * (coeficientes.picoTarde || 0);
    } else if (options && options.length > 0 && (options[0].label.includes("AM") || options[0].label.includes("PM"))) {
      const coeficientes = coeficientesFrecuencia[frecuenciaNumero] || {};
      impactosTotalCowork =
        totalContactosCampañaCowork * valle * (coeficientes.valle || 0) +
        totalContactosCampañaCowork * picoMañana * (coeficientes.picoMañana || 0) +
        totalContactosCampañaCowork * picoMediodia * (coeficientes.picoMediodia || 0) +
        totalContactosCampañaCowork * picoTarde * (coeficientes.picoTarde || 0);
    }
    impactosTotalCowork = Math.round(impactosTotalCowork * 100) / 100;

    // Espera
    let impactosTotalEspera = 0;
    if (intervalos && intervalos.length > 0) {
      const coeficientes = coeficientesFrecuencia[frecuenciaNumero] || {};
      if (countValle > 0) impactosTotalEspera += totalContactosValleEspera * (coeficientes.valle || 0);
      if (countMañana > 0 || countTarde > 0)
        impactosTotalEspera +=
          (totalContactosMañanaEspera + totalContactosTardeEspera) * (coeficientes.picoMañana || 0);
      if (countMediodia > 0) impactosTotalEspera += totalContactosMediodiaEspera * (coeficientes.picoMediodia || 0);
    } else if (options && options.length > 0 && options[0].label.includes("FULL")) {
      const coeficientes = coeficientesFrecuencia[frecuenciaNumero] || {};
      impactosTotalEspera =
        totalContactosCampañaEspera * valle * (coeficientes.valle || 0) +
        totalContactosCampañaEspera * picoMañana * (coeficientes.picoMañana || 0) +
        totalContactosCampañaEspera * picoMediodia * (coeficientes.picoMediodia || 0) +
        totalContactosCampañaEspera * picoTarde * (coeficientes.picoTarde || 0);
    } else if (options && options.length > 0 && (options[0].label.includes("AM") || options[0].label.includes("PM"))) {
      const coeficientes = coeficientesFrecuencia[frecuenciaNumero] || {};
      impactosTotalEspera =
        totalContactosCampañaEspera * valle * (coeficientes.valle || 0) +
        totalContactosCampañaEspera * picoMañana * (coeficientes.picoMañana || 0) +
        totalContactosCampañaEspera * picoMediodia * (coeficientes.picoMediodia || 0) +
        totalContactosCampañaEspera * picoTarde * (coeficientes.picoTarde || 0);
    }
    impactosTotalEspera = Math.round(impactosTotalEspera * 100) / 100;

    // --- CPM ---
    const duracionIncidenciaCotizacion = DURACION_INCIDENCIA[duracion] || 0;
    const cpmReferencia = CPM_REFERENCIA_BASE * duracionIncidenciaCotizacion;

    const exchange = forceDollars ? 1 : obtenerTipoDeCambio();
    const multiplier =
      intervalos.length > 0
        ? MULTIPLICADOR_TIPO_CAMPANA.intervalos
        : options && options.length > 0 && (options[0].label.includes("AM") || options[0].label.includes("PM"))
          ? MULTIPLICADOR_TIPO_CAMPANA.amPm
          : MULTIPLICADOR_TIPO_CAMPANA.full;
    const cpmAscensores = (cpmReferencia * (forceDollars ? 1 : exchange) * multiplier) / 1000;
    const cpmTotems = cpmAscensores * CPM_RATIO_OTROS_CIRCUITOS;
    const cpmCowork = cpmAscensores * CPM_RATIO_OTROS_CIRCUITOS;
    const cpmEspera = cpmAscensores * CPM_RATIO_OTROS_CIRCUITOS;

    // --- Inversiones brutas ---
    const inversionAscensores = impactosTotalAscensores * cpmAscensores;
    const inversionTotems = impactosTotalTotems * cpmTotems;
    const inversionCowork = impactosTotalCowork * cpmCowork;
    const inversionEspera = impactosTotalEspera * cpmEspera;

    // Salidas e impactos totales
    const salidasTotales = salidasAscensores + salidasTotems + salidasCowork + salidasEspera;
    const impactosTotales =
      (impactosTotalAscensores || 0) +
      (impactosTotalTotems || 0) +
      (impactosTotalCowork || 0) +
      (impactosTotalEspera || 0);

    // --- Moneda ---
    let monedaActual;
    if (forceDollars) {
      monedaActual = "USD";
    } else if (pais === "Chile") {
      monedaActual = "CLP";
    } else if (pais === "Perú") {
      monedaActual = "PEN";
    } else {
      monedaActual = "USD";
    }
    const selectedMoneda = getMonedaSymbol(monedaActual);

    // --- Descuentos del sistema ---
    let descuentoDuracion = DESCUENTO_DURACION[duracion] || 0;
    if (duracion > 12 && horasFinal > 12) {
      descuentoDuracion += descuentoDuracion * BONUS_DURACION_HORAS_LARGAS;
    }

    // Descuento por inventario
    const totalPantallasDisponibles = ascensoresTotal + totemsTotal + coworkTotal + esperaTotal;
    const totalPantallasSeleccionadas =
      ascensoresSeleccionados + totemsSeleccionados + coworkSeleccionados + esperaSeleccionados;
    let invRatio = 0;
    if (totalPantallasDisponibles > 0) {
      invRatio = totalPantallasSeleccionadas / totalPantallasDisponibles;
    }
    let descuentoInventario = invRatio * DESCUENTO_MAXIMO_INVENTARIO;
    descuentoInventario = Math.min(descuentoInventario, DESCUENTO_MAXIMO_INVENTARIO);
    if (invRatio < INVENTARIO_RATIO_MINIMO) descuentoInventario *= INVENTARIO_RATIO_MINIMO;
    const descuentoTotalPorInventario = descuentoInventario;

    // Descuento por plazo
    const effectivePlazo = Math.min(plazo, PLAZO_MAXIMO_EFECTIVO);
    const normPlazo = 1 - Math.exp(-PLAZO_MAXIMO_EFECTIVO / TAU_AMORTIGUAMIENTO);
    let descuentoPlazo = (DESCUENTO_MAXIMO_PLAZO * (1 - Math.exp(-effectivePlazo / TAU_AMORTIGUAMIENTO))) / normPlazo;
    if (effectivePlazo < 20) descuentoPlazo = Math.max(descuentoPlazo, PLAZO_MINIMO_DESCUENTO);
    if (invRatio > 0.7 && effectivePlazo > 60) {
      descuentoPlazo *= PLAZO_INVENTARIO_BONUS;
    }

    // Descuento por frecuencia
    let descuentoFrecuenciaNuevo = 0;
    if (frecuenciaNumero === 5) {
      descuentoFrecuenciaNuevo = FRECUENCIA_BASE_DESCUENTO;
    } else if (frecuenciaNumero >= 10 && frecuenciaNumero <= 30 && frecuenciaNumero % 5 === 0) {
      const incremento = (frecuenciaNumero - 5) / 5;
      descuentoFrecuenciaNuevo =
        (FRECUENCIA_BASE_DESCUENTO + incremento * FRECUENCIA_INCREMENTO) * FRECUENCIA_FACTOR_REDUCCION;
    }
    const descuentoFrecuenciaNuevoHoras =
      horasFinal === 7 ? descuentoFrecuenciaNuevo / FRECUENCIA_DIVISOR_HORAS_7 : descuentoFrecuenciaNuevo;

    let bonoFreqPlazo = 0;
    if (frecuenciaNumero === 5 && plazo >= 20) bonoFreqPlazo = 0.01;
    else if (frecuenciaNumero === 10 && plazo >= 10 && plazo < 20) bonoFreqPlazo = 0.015;
    else if (frecuenciaNumero === 15 && plazo >= 5 && plazo < 10) bonoFreqPlazo = 0.02;
    else if (frecuenciaNumero === 20 && plazo < 5) bonoFreqPlazo = 0.025;
    else if ((frecuenciaNumero === 25 || frecuenciaNumero === 30) && horasFinal < 14 && plazo < 20)
      bonoFreqPlazo = 0.03;

    let multiplicadorFreqPlazoInv = 1;
    if (bonoFreqPlazo > 0 && (effectivePlazo > 60 || invRatio > 0.7)) {
      multiplicadorFreqPlazoInv = BONO_FREQ_PLAZO_MULTIPLICADOR;
    }

    let penalizacionFreqInadecuada = 1;
    if (frecuenciaNumero < 15 && effectivePlazo > 60) {
      penalizacionFreqInadecuada = PENALIZACION_FREQ_INADECUADA;
    }

    const descuentoFrecAjustado =
      (descuentoFrecuenciaNuevoHoras + bonoFreqPlazo) * multiplicadorFreqPlazoInv * penalizacionFreqInadecuada;

    // Descuento global (sistema)
    const newDescuentoGlobal = descuentoDuracion + descuentoTotalPorInventario + descuentoPlazo + descuentoFrecAjustado;
    const baseDescuentoLimitado = Math.min(newDescuentoGlobal, CAP_DESCUENTO_BASE);
    const scaleFactor = SCALE_FACTOR_PAIS[pais] || SCALE_FACTOR_PAIS.Region;
    const descuentoGlobal = baseDescuentoLimitado * scaleFactor;

    // --- Inversiones finales por circuito (con descuento del sistema) ---
    const inversionAscensoresFinal = inversionAscensores * (1 - descuentoGlobal);
    const inversionTotemsFinal = inversionTotems * (1 - descuentoGlobal);
    const inversionCoworkFinal = inversionCowork * (1 - descuentoGlobal);
    const inversionEsperaFinal = inversionEspera * (1 - descuentoGlobal);

    // Tarifas frías (brutas)
    const tarifaFriaAscensores = inversionAscensores;
    const tarifaFriaTotems = inversionTotems;
    const tarifaFriaCowork = inversionCowork;
    const tarifaFriaEspera = inversionEspera;
    const tarifaFriaTotal =
      (tarifaFriaAscensores || 0) + (tarifaFriaTotems || 0) + (tarifaFriaCowork || 0) + (tarifaFriaEspera || 0);

    // Base total (sin descuento manual)
    const baseInversionFinalTotal =
      (inversionAscensoresFinal || 0) +
      (inversionTotemsFinal || 0) +
      (inversionCoworkFinal || 0) +
      (inversionEsperaFinal || 0);

    // --- Descuento manual ---
    let effectiveManualDiscountAmount = 0;
    let effectiveFinalAmount = baseInversionFinalTotal;

    if (discountType === "percentage") {
      effectiveManualDiscountAmount = (discountPercentage / 100) * baseInversionFinalTotal;
    } else if (discountType === "amount") {
      effectiveManualDiscountAmount = manualDiscountAmount;
    } else if (discountType === "final") {
      effectiveFinalAmount = finalAmount;
      effectiveManualDiscountAmount = baseInversionFinalTotal - effectiveFinalAmount;
    }

    effectiveManualDiscountAmount = Math.min(effectiveManualDiscountAmount, baseInversionFinalTotal);
    effectiveFinalAmount = Math.max(0, baseInversionFinalTotal - effectiveManualDiscountAmount);

    const inversionFinalTotal = effectiveFinalAmount;

    // CPM final por circuito (sin descuento manual)
    const cpmAscensoresFinal =
      impactosTotalAscensores > 0 ? (inversionAscensoresFinal / impactosTotalAscensores) * 1000 : 0;
    const cpmTotemsFinal = impactosTotalTotems > 0 ? (inversionTotemsFinal / impactosTotalTotems) * 1000 : 0;
    const cpmCoworkFinal = impactosTotalCowork > 0 ? (inversionCoworkFinal / impactosTotalCowork) * 1000 : 0;
    const cpmEsperaFinal = impactosTotalEspera > 0 ? (inversionEsperaFinal / impactosTotalEspera) * 1000 : 0;
    const cpmTotalFinal = impactosTotales > 0 ? (effectiveFinalAmount / impactosTotales) * 1000 : 0;

    const effectiveDiscountPercentage =
      baseInversionFinalTotal > 0 ? (effectiveManualDiscountAmount / baseInversionFinalTotal) * 100 : 0;

    return {
      horasFinal,
      salidasAscensores,
      salidasTotems,
      salidasCowork,
      salidasEspera,
      salidasTotales,
      contactosCampañaAscensores,
      contactosCampañaTotems,
      contactosCampañaCowork,
      contactosCampañaEspera,
      contactosCampañaTotal,
      impactosTotalAscensores,
      impactosTotalTotems,
      impactosTotalCowork,
      impactosTotalEspera,
      impactosTotales,
      inversionAscensoresFinal,
      inversionTotemsFinal,
      inversionCoworkFinal,
      inversionEsperaFinal,
      inversionFinalTotal,
      tarifaFriaAscensores,
      tarifaFriaTotems,
      tarifaFriaCowork,
      tarifaFriaEspera,
      tarifaFriaTotal,
      cpmAscensoresFinal,
      cpmTotemsFinal,
      cpmCoworkFinal,
      cpmEsperaFinal,
      cpmTotalFinal,
      descuentoGlobal,
      selectedMoneda,
      baseInversionFinalTotal,
      effectiveManualDiscountAmount,
      effectiveFinalAmount,
      effectiveDiscountPercentage,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ascensoresSeleccionados,
    totemsSeleccionados,
    coworkSeleccionados,
    esperaSeleccionados,
    audienciaSeleccionadaAscensor,
    audienciaSeleccionadaTotems,
    audienciaSeleccionadaCowork,
    audienciaSeleccionadaEspera,
    plazo,
    frecuencia,
    horasSeleccionadas,
    duracion,
    pais,
    ascensoresTotal,
    totemsTotal,
    coworkTotal,
    esperaTotal,
    forceDollars,
    forceRecalc,
    discountType,
    discountPercentage,
    manualDiscountAmount,
    finalAmount,
    obtenerTipoDeCambio,
    getMonedaSymbol,
  ]);
}
